/**
 * Auth API helpers — matches backend OpenAPI `/api/v1/auth/*`:
 *
 *   POST   /api/v1/auth/login                      → loginWithPassword
 *   POST   /api/v1/auth/refresh                    → refreshAccessToken (http.ts)
 *   POST   /api/v1/auth/logout                     → logoutRequest
 *   GET    /api/v1/auth/sessions                   → fetchSessions
 *   DELETE /api/v1/auth/sessions/{session_id}      → revokeSession
 *
 * Login uses OAuth2 password form body:
 *   Content-Type: application/x-www-form-urlencoded
 *   username=&password=
 * Response (TokenResponseSchema):
 *   { accessToken, tokenType?, userId, username }
 *
 * /auth/refresh and /auth/logout authenticate via the HttpOnly refresh_token
 * cookie (sent automatically) plus an X-CSRF-Token header — see csrf.ts.
 * /auth/sessions* use the normal Bearer access token.
 */
import axios from 'axios';
import { http, refreshAccessToken } from './http';
import {
  getAuthLoginUrl,
  getAuthLogoutUrl,
  getAuthSessionUrl,
  getAuthSessionsUrl,
} from './apiConfig';
import { csrfHeaderBag } from './csrf';
import { parseAuthResponse, type AuthResult } from './authResponse';

export interface LoginCredentials {
  username: string;
  password: string;
}

export type LoginResult = AuthResult;

/** One row from GET /auth/sessions (SessionResponseSchema). */
export interface SessionInfo {
  sessionId: string;
  createdAt: string;
  lastUsedAt: string;
  userAgent: string | null;
  ipAddress: string | null;
  deviceName: string | null;
  isCurrent: boolean;
}

const DEFAULT_LOGIN_TIMEOUT_MS = 30000;

function readLoginTimeoutMs(): number {
  const raw = import.meta.env.VITE_LOGIN_TIMEOUT_MS;
  if (typeof raw !== 'string') return DEFAULT_LOGIN_TIMEOUT_MS;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_LOGIN_TIMEOUT_MS;
}

function asOptionalString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function toErrorMessage(err: unknown, url: string): string {
  if (axios.isAxiosError(err)) {
    if (err.code === 'ECONNABORTED' || /timeout/i.test(err.message)) {
      return `Login timed out contacting ${url}. Check that the API is reachable and restart the dev server if you changed VITE_API_BASE_URL.`;
    }
    if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
      return `Cannot reach login API at ${url}. Check the backend / network, or use the Vite proxy (/api/v1).`;
    }

    if (err.response?.status === 429) {
      return 'Too many attempts. Please wait a minute before trying again.';
    }

    const data = err.response?.data as unknown;
    if (data && typeof data === 'object') {
      const detail = (data as { detail?: unknown }).detail;
      if (typeof detail === 'string' && detail.trim()) return detail;
      if (Array.isArray(detail) && detail.length > 0) {
        const first = detail[0] as { msg?: unknown };
        if (typeof first?.msg === 'string' && first.msg.trim()) return first.msg;
      }
      const message = (data as { message?: unknown }).message;
      if (typeof message === 'string' && message.trim()) return message;
    }
    if (err.response?.status === 401) return 'Invalid username or password.';
    if (err.response?.status === 403) return 'Access denied.';
    if (err.message) return err.message;
  }
  if (err instanceof Error && err.message) return err.message;
  return 'Login failed. Please try again.';
}

function mapSession(raw: unknown): SessionInfo | null {
  if (!raw || typeof raw !== 'object') return null;
  const record = raw as Record<string, unknown>;
  const sessionId =
    asOptionalString(record.sessionId) ?? asOptionalString(record.session_id);
  const createdAt =
    asOptionalString(record.createdAt) ?? asOptionalString(record.created_at);
  const lastUsedAt =
    asOptionalString(record.lastUsedAt) ?? asOptionalString(record.last_used_at);
  if (!sessionId || !createdAt || !lastUsedAt) return null;
  return {
    sessionId,
    createdAt,
    lastUsedAt,
    userAgent:
      asOptionalString(record.userAgent) ?? asOptionalString(record.user_agent),
    ipAddress:
      asOptionalString(record.ipAddress) ?? asOptionalString(record.ip_address),
    deviceName:
      asOptionalString(record.deviceName) ?? asOptionalString(record.device_name),
    isCurrent: Boolean(record.isCurrent ?? record.is_current),
  };
}

/** POST /api/v1/auth/login */
export async function loginWithPassword(
  credentials: LoginCredentials,
  options?: { url?: string; timeoutMs?: number }
): Promise<LoginResult> {
  const username = credentials.username.trim();
  const password = credentials.password;
  if (!username || !password) {
    throw new Error('Username and password are required.');
  }

  const url = options?.url || getAuthLoginUrl();
  const body = new URLSearchParams();
  body.set('username', username);
  body.set('password', password);

  try {
    const res = await http.post(url, body.toString(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      timeout: options?.timeoutMs ?? readLoginTimeoutMs(),
    });
    const result = parseAuthResponse(res.data, username);
    if (!result) {
      throw new Error('Login response is missing accessToken.');
    }
    return result;
  } catch (err) {
    throw new Error(toErrorMessage(err, url), { cause: err });
  }
}

/**
 * POST /api/v1/auth/refresh — rotate refresh cookie, return new access token.
 * Implemented in http.ts (shared with the 401 interceptor).
 */
export { refreshAccessToken };

/**
 * POST /api/v1/auth/logout — revoke current refresh session + clear cookies.
 * Never throws: local logout must always proceed.
 */
export async function logoutRequest(): Promise<void> {
  try {
    await http.post(getAuthLogoutUrl(), null, {
      headers: csrfHeaderBag(),
    });
  } catch {
    // Intentionally swallowed — see doc comment above.
  }
}

/** GET /api/v1/auth/sessions */
export async function fetchSessions(): Promise<SessionInfo[]> {
  const res = await http.get<{ sessions?: unknown[] }>(getAuthSessionsUrl());
  const rows = Array.isArray(res.data?.sessions) ? res.data.sessions : [];
  return rows.map(mapSession).filter((s): s is SessionInfo => Boolean(s));
}

/** DELETE /api/v1/auth/sessions/{session_id} */
export async function revokeSession(sessionId: string): Promise<void> {
  const id = sessionId.trim();
  if (!id) throw new Error('sessionId is required.');
  await http.delete(getAuthSessionUrl(id));
}
